/**
 * Module dependencies.
 */

import { ChevronRight, Clock, FileText, FolderPlus, Search, Star, Trash2, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { NoteFile, useGetNotesFileSystem } from 'app/hooks/requests/apps-notes/use-get-notes-file-system';
import { NotesSelection } from './types';
import { AddFolder } from './add-folder';
import { AddFile } from './add-file';
import { useTranslation } from 'react-i18next';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { usePostSortNotes } from 'app/hooks/requests/apps-notes/use-post-sort-notes';
import { FileButton } from './file-button';
import { cn } from 'app/lib/utils';

/**
 * Flatten a tree into a single list.
 */

function flatten(tree: NoteFile[]): NoteFile[] {
  return tree.flatMap(node => [node, ...(node.children ? flatten(node.children) : [])]);
}

/**
 * Keep folders only (recursively) — the library navigates structure, not files.
 */

function foldersOnly(tree: NoteFile[]): NoteFile[] {
  return tree
    .filter(node => !node.isLeaf)
    .map(node => ({ ...node, children: node.children ? foldersOnly(node.children) : undefined }));
}

/**
 * `DropPosition` type.
 */

type DropPosition = 'before' | 'inside' | 'after';

/**
 * Find siblings of a node.
 */

function findSiblings(tree: NoteFile[], targetKey: string): NoteFile[] {
  function recurse(nodes: NoteFile[]): NoteFile[] {
    for (const node of nodes) {
      if (node.children?.some(child => child.key === targetKey)) {
        return node.children ?? [];
      }

      if (node.children) {
        const found = recurse(node.children);

        if (found.length) {
          return found;
        }
      }
    }

    return [];
  }

  if (tree.some(node => node.key === targetKey)) {
    return tree;
  }

  return recurse(tree);
}

/**
 * `NavRow` component — a smart-section entry.
 */

function NavRow({
  active,
  count,
  icon,
  label,
  onClick
}: {
  active: boolean;
  count?: number;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
        active && 'bg-primary/10 font-medium text-primary hover:bg-primary/10 hover:text-primary'
      )}
      onClick={onClick}
      type={'button'}
    >
      {icon}

      <span className={'min-w-0 flex-1 truncate'}>{label}</span>

      {count !== undefined && count > 0 && (
        <span className={'shrink-0 text-[11px] tabular-nums opacity-70'}>{count}</span>
      )}
    </button>
  );
}

/**
 * `FolderNode` component — a foldable folder row (folders only).
 */

type FolderNodeProps = {
  node: NoteFile;
  level: number;
  expandedKeys: Set<string>;
  activeKey: string | null;
  draggingKey: string | null;
  onToggle: (key: string) => void;
  onSelect: (node: NoteFile) => void;
  onDragStart: (node: NoteFile) => void;
  onDragEnd: () => void;
  onDropNode: (node: NoteFile, position: DropPosition) => void;
};

function FolderNode({
  activeKey,
  draggingKey,
  expandedKeys,
  level,
  node,
  onDragEnd,
  onDragStart,
  onDropNode,
  onSelect,
  onToggle
}: FolderNodeProps): JSX.Element {
  const [drop, setDrop] = useState<DropPosition | null>(null);
  const isExpanded = expandedKeys.has(node.key);
  const hasChildren = !!node.children && node.children.length > 0;

  const computePosition = (event: React.DragEvent<HTMLDivElement>): DropPosition => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientY - rect.top) / rect.height;

    if (ratio < 0.25) {
      return 'before';
    }

    if (ratio > 0.75) {
      return 'after';
    }

    return 'inside';
  };

  return (
    <div>
      <div
        className={cn(
          'group flex cursor-pointer select-none items-center gap-1 rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-accent hover:text-foreground',
          activeKey === node.key && 'bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary',
          draggingKey === node.key && 'opacity-40',
          drop === 'inside' && 'ring-1 ring-inset ring-primary/40',
          drop === 'before' && 'border-t border-primary',
          drop === 'after' && 'border-b border-primary'
        )}
        draggable
        onClick={() => onSelect(node)}
        onDragEnd={() => {
          setDrop(null);
          onDragEnd();
        }}
        onDragLeave={() => setDrop(null)}
        onDragOver={event => {
          event.preventDefault();
          setDrop(computePosition(event));
        }}
        onDragStart={() => onDragStart(node)}
        onDrop={event => {
          event.preventDefault();
          event.stopPropagation();
          const position = computePosition(event);
          setDrop(null);
          onDropNode(node, position);
        }}
        style={{ paddingLeft: level * 14 + 6 }}
      >
        <button
          className={cn('flex h-4 w-4 shrink-0 items-center justify-center', !hasChildren && 'invisible')}
          onClick={event => {
            event.stopPropagation();
            onToggle(node.key);
          }}
          type={'button'}
        >
          <ChevronRight className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-90')} />
        </button>

        <div className={'min-w-0 flex-1'}>
          <FileButton node={{ ...node, isLeaf: false }} />
        </div>
      </div>

      {hasChildren && isExpanded && node.children && (
        <div>
          {node.children.map(child => (
            <FolderNode
              activeKey={activeKey}
              draggingKey={draggingKey}
              expandedKeys={expandedKeys}
              key={child.key}
              level={level + 1}
              node={child}
              onDragEnd={onDragEnd}
              onDragStart={onDragStart}
              onDropNode={onDropNode}
              onSelect={onSelect}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * `Props` type.
 */

type Props = {
  selection: NotesSelection;
  onSelectionChange: (selection: NotesSelection) => void;
  query: string;
  onQueryChange: (query: string) => void;
  parentIdForNew?: string | null;
};

/**
 * Export `Files` component — the Library column (navigation only: search, smart
 * sections and the folder tree). Individual notes live in the middle column.
 */

export function Files({ onQueryChange, onSelectionChange, parentIdForNew, query, selection }: Props): JSX.Element {
  const { data: files, isLoading } = useGetNotesFileSystem();
  const { mutateAsync: sortItems } = usePostSortNotes();
  const [translate] = useTranslation();
  const { error } = useToast();
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const dragNodeRef = React.useRef<NoteFile | null>(null);

  const folders = useMemo(() => foldersOnly(files ?? []), [files]);
  const favoritesCount = useMemo(
    () => flatten(files ?? []).filter(node => node.isLeaf && node.favorite).length,
    [files]
  );

  const activeFolderKey = selection.type === 'folder' ? selection.key : null;

  const handleToggle = (key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  };

  const onDrop = async (dragNode: NoteFile, dropNode: NoteFile, position: DropPosition) => {
    if (!files) {
      return;
    }

    let newParentId: string | null = null;
    let beforeId: string | undefined;
    let afterId: string | undefined;

    if (position === 'inside') {
      newParentId = dropNode.key;
      const firstChild = dropNode.children?.[0];

      if (firstChild) {
        afterId = firstChild.key;
      }
    } else {
      const siblings = findSiblings(files, dropNode.key);
      const index = siblings.findIndex(node => node.key === dropNode.key);
      newParentId = dropNode.parent_id || null;

      if (position === 'before') {
        afterId = dropNode.key;
        const prev = siblings[index - 1];

        if (prev) {
          beforeId = prev.key;
        }
      } else {
        beforeId = dropNode.key;
        const next = siblings[index + 1];

        if (next) {
          afterId = next.key;
        }
      }
    }

    try {
      await sortItems({ afterId, beforeId, itemId: dragNode.key, newParentId });
    } catch (err) {
      error({ message: translate('notes.errors.moveFailed') });
    }
  };

  const onDropNode = (node: NoteFile, position: DropPosition) => {
    const dragNode = dragNodeRef.current;

    if (!dragNode || dragNode.key === node.key) {
      return;
    }

    onDrop(dragNode, node, position);
  };

  return (
    <div className={'flex h-full flex-col'}>
      {/* Search */}
      <div className={'relative mb-3'}>
        <Search
          className={'pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground'}
        />

        <input
          className={
            'h-9 w-full rounded-md border border-input bg-background pl-8 pr-8 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring'
          }
          onChange={event => onQueryChange(event.target.value)}
          placeholder={translate('notes.search.placeholder')}
          type={'text'}
          value={query}
        />

        {!!query && (
          <button
            className={'absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'}
            onClick={() => onQueryChange('')}
            title={translate('notes.search.clear')}
            type={'button'}
          >
            <X className={'h-4 w-4'} />
          </button>
        )}
      </div>

      {/* Smart sections */}
      <div className={'space-y-0.5'}>
        <NavRow
          active={selection.type === 'all'}
          icon={<FileText className={'h-4 w-4 shrink-0'} />}
          label={translate('notes.list.allNotes')}
          onClick={() => onSelectionChange({ type: 'all' })}
        />

        <NavRow
          active={selection.type === 'favorites'}
          count={favoritesCount}
          icon={<Star className={'h-4 w-4 shrink-0'} />}
          label={translate('notes.sections.favorites')}
          onClick={() => onSelectionChange({ type: 'favorites' })}
        />

        <NavRow
          active={selection.type === 'recent'}
          icon={<Clock className={'h-4 w-4 shrink-0'} />}
          label={translate('notes.sections.recent')}
          onClick={() => onSelectionChange({ type: 'recent' })}
        />

        <NavRow
          active={selection.type === 'trash'}
          icon={<Trash2 className={'h-4 w-4 shrink-0'} />}
          label={translate('notes.trash.title')}
          onClick={() => onSelectionChange({ type: 'trash' })}
        />
      </div>

      {/* Folders */}
      <div className={'mb-1 mt-4 flex items-center justify-between px-2'}>
        <span className={'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'}>
          {translate('notes.nav.folders')}
        </span>

        <div className={'flex items-center gap-1 text-muted-foreground'}>
          <AddFolder
            trigger={(open: () => void) => (
              <button
                className={'rounded p-1 hover:bg-accent hover:text-foreground'}
                onClick={open}
                title={translate('notes.actions.newFolder')}
                type={'button'}
              >
                <FolderPlus className={'h-4 w-4'} />
              </button>
            )}
          />

          <AddFile
            parentId={parentIdForNew}
            trigger={open => (
              <button
                className={'rounded p-1 hover:bg-accent hover:text-foreground'}
                onClick={open}
                title={translate('notes.actions.newFile')}
                type={'button'}
              >
                <FileText className={'h-4 w-4'} />
              </button>
            )}
          />
        </div>
      </div>

      <div className={'-mx-1 flex-1 overflow-y-auto px-1'}>
        {isLoading ? (
          <div className={'space-y-2 p-2'}>
            {Array.from({ length: 4 }).map((_, index) => (
              <div className={'h-6 animate-pulse rounded bg-muted'} key={index} />
            ))}
          </div>
        ) : folders.length === 0 ? (
          <p className={'px-2 py-3 text-xs text-muted-foreground'}>{translate('notes.nav.noFolders')}</p>
        ) : (
          folders.map(node => (
            <FolderNode
              activeKey={activeFolderKey}
              draggingKey={draggingKey}
              expandedKeys={expandedKeys}
              key={node.key}
              level={0}
              node={node}
              onDragEnd={() => {
                dragNodeRef.current = null;
                setDraggingKey(null);
              }}
              onDragStart={dragNode => {
                dragNodeRef.current = dragNode;
                setDraggingKey(dragNode.key);
              }}
              onDropNode={onDropNode}
              onSelect={selected =>
                onSelectionChange({
                  key: selected.key,
                  name: typeof selected.title === 'string' ? selected.title : '',
                  type: 'folder'
                })
              }
              onToggle={handleToggle}
            />
          ))
        )}
      </div>
    </div>
  );
}
